import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  FileText,
  ArrowLeftRight,
  PlusCircle,
  Hash,
  QrCode,
  FileSpreadsheet,
  RotateCw,
  Building2,
  Tag,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import {
  ODataGrid,
  type ODataGridConfig,
  type GridToolbarHelpers,
} from '@inuxe/prime-odata-grid';
import CommonButton from '../../../components/common-components/button/CommonButton';
import DeleteHospitalRecordDialog from './parts/DeleteHospitalRecordDialog';
import { useStore } from '../../../hooks/useStore';
import type { IHospitalRecordResult } from '../../../models/hospital/HospitalModel';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import gridStyles from '../../../styles/ODataGridPage.module.css';
import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../utils/odataGridUtils';
import { getHospitalRecordColorRowClassName } from '../../../utils/gridRowClassNames';
import { HOSPITAL_RECORD_DEFAULT_SORT_MODEL } from '../../../store/modules/hospital-files/HospitalFilesStore';
import { useHospitalFilesColumns } from './HospitalFiles.grid';
import styles from './HospitalFiles.module.css';
import NewRowManuel from './parts/new-data/new-row/manuel/NewRowManuel';
import NewRowQrCode from './parts/new-data/new-row/qr-code/NewRowQrCode';
import EditRowManually from './parts/edit-data/EditRowManually';
import SwitchHospital from './parts/switch-data/SwitchHospital';
import NewRowFromKn from './parts/new-data/new-row/from-kn/NewRowFromKn';

const GRID_ID = 'hospital-files-grid';

const HospitalFiles = () => {
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const { popupStore, hospitalFilesStore } = useStore();
  const lastGridScopeKeyRef = useRef<string | null>(null);

  const colorItems = useMemo<
    { key: string; label: string; dot: string; value: number; badgeClass: string }[]
  >(() => {
    const map = new Map<string, number>();
    (hospitalFilesStore.tagColors || []).forEach(c => {
      if (c?.color) {
        map.set(c.color.toLowerCase(), c.sequence ?? 0);
      }
    });
    return [
      { key: 'red', label: 'Kırmızı', dot: '#ef4444', badgeClass: styles.colorBadgeRed },
      { key: 'yellow', label: 'Sarı', dot: '#eab308', badgeClass: styles.colorBadgeYellow },
      { key: 'green', label: 'Yeşil', dot: '#22c55e', badgeClass: styles.colorBadgeGreen },
    ].map(x => ({ ...x, value: map.get(x.key) ?? 0 }));
  }, [hospitalFilesStore.tagColors]);

  useEffect(() => {
    if (!hospitalFilesStore.selectedHospital) {
      popupStore.setIsSwitchHospitalOpen(true);
    }
    hospitalFilesStore.startLiveTagColors();
    return () => {
      hospitalFilesStore.stopLiveTagColors();
    };
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    hospitalFilesStore.getTagColors();
  }, [hospitalFilesStore.selectedTag?.id]);

  const isMobile = viewportWidth < 768;
  const pageSize = isMobile ? 10 : 20;
  const columns = useHospitalFilesColumns();

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IHospitalRecordResult>>(
    () => ({
      gridId: GRID_ID,
      rows: pageSize,
      initialLazyState: { sortField: 'id', sortOrder: -1 },
      oDataMapping,
      fetchRows: async params => {
        if (!hospitalFilesStore.selectedHospital) {
          return { rows: [], totalRecords: 0 };
        }
        const result = await hospitalFilesStore.listHospitalRecord(
          buildLegacyGridParams(params, { oDataMapping })
        );
        return { rows: result.rows, totalRecords: result.lastRow };
      },
    }),
    [hospitalFilesStore, hospitalFilesStore.selectedHospital, hospitalFilesStore.selectedTag, pageSize, oDataMapping]
  );

  const onGridReady = useCallback(
    (helpers: GridToolbarHelpers<IHospitalRecordResult>) => {
      hospitalFilesStore.setGridHelpers(helpers);
    },
    [hospitalFilesStore]
  );

  useEffect(() => {
    const scopeKey = `${hospitalFilesStore.selectedHospital?.id ?? ''}-${hospitalFilesStore.selectedTag?.id ?? ''}`;
    if (!hospitalFilesStore.gridHelpers) {
      lastGridScopeKeyRef.current = scopeKey;
      return;
    }
    if (lastGridScopeKeyRef.current === scopeKey) {
      return;
    }
    lastGridScopeKeyRef.current = scopeKey;
    hospitalFilesStore.gridHelpers.refreshGrid();
  }, [
    hospitalFilesStore.selectedHospital?.id,
    hospitalFilesStore.selectedTag?.id,
    hospitalFilesStore.gridHelpers,
  ]);

  const exportToExcel = useCallback(
    async (helpers: GridToolbarHelpers<IHospitalRecordResult>) => {
      try {
        const queryState = helpers.getQueryState({ skip: 0, top: 1000000 });
        const legacyParams = buildLegacyGridParams(queryState);
        const result = await hospitalFilesStore.listHospitalRecord({
          ...legacyParams,
          sortModel: HOSPITAL_RECORD_DEFAULT_SORT_MODEL,
        });

        if (!result?.rows.length) {
          return;
        }

        const exportData = result.rows.map(item => ({
          ID: item.id,
          Hastane: item.hospitalName,
          'Cihaz Tanımı': item.biomedicalDeviceDescription,
          Marka: item.brand,
          Model: item.model,
          'Seri No': item.serialNumber,
          'Künye No': item.identificationNumber,
          Lokasyon: item.location,
          'Etiket No': item.tagNumberWithColor,
          'Eklenme Tarihi': new Date(item.addDate).toLocaleDateString('tr-TR'),
          'Zimmetli Kişi': item.responsiblePerson,
          Notlar: item.note,
          'Ekleyen Kişi': item.createdBy,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Hastane Cihazları');

        const hospitalName = hospitalFilesStore.selectedHospital?.hospitalName || 'Hastane';
        const sanitizedHospitalName = hospitalName
          .replace(/[ıİ]/g, 'I')
          .replace(/[ğĞ]/g, 'G')
          .replace(/[üÜ]/g, 'U')
          .replace(/[şŞ]/g, 'S')
          .replace(/[öÖ]/g, 'O')
          .replace(/[çÇ]/g, 'C')
          .replace(/[^a-zA-Z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '');

        const dateStr = new Date().toISOString().split('T')[0];
        const fileName = sanitizedHospitalName
          ? `${sanitizedHospitalName}_Hastane_Cihazlari_${dateStr}.xlsx`
          : `Hastane_Cihazlari_${dateStr}.xlsx`;

        XLSX.writeFile(wb, fileName);
      } catch (error) {
        console.error('Excel export error:', error);
      }
    },
    [hospitalFilesStore]
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <span className={styles.titleIcon} aria-hidden="true">
              <FileText size={24} strokeWidth={2} />
            </span>
            <div className={styles.titleContent}>
              <h2 className={styles.mainTitle}>Hastane Dosyaları</h2>
              <p className={styles.pageDescription}>
                Seçili hastane ve etiket için cihaz kayıtlarını görüntüleyin ve yönetin.
              </p>
            </div>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statCardHeader}>
                <span className={styles.statIcon} aria-hidden="true">
                  <Building2 size={15} strokeWidth={2} />
                </span>
                <span className={styles.statLabel}>Seçilen Hastane</span>
              </div>
              <p className={styles.statValue} title={hospitalFilesStore.selectedHospital?.hospitalName}>
                {hospitalFilesStore.selectedHospital?.hospitalName ?? '—'}
              </p>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statCardHeader}>
                <span className={styles.statIcon} aria-hidden="true">
                  <Tag size={15} strokeWidth={2} />
                </span>
                <span className={styles.statLabel}>Seçilen Etiket No</span>
              </div>
              <p className={styles.statValue}>{hospitalFilesStore.selectedTag?.prefix ?? '—'}</p>
            </div>

            <div className={`${styles.statCard} ${styles.statCardWide}`}>
              <div className={styles.statCardHeader}>
                <span className={styles.statLabel}>Güncel Etiket Sırası</span>
              </div>
              <div className={styles.colorBadgeRow}>
                {colorItems.map(item => (
                  <span key={item.key} className={`${styles.colorBadge} ${item.badgeClass}`}>
                    <span className={styles.colorBadgeDot} style={{ backgroundColor: item.dot }} />
                    <span className={styles.colorBadgeLabel}>{item.label}</span>
                    <span className={styles.colorBadgeValue}>{item.value}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <ODataGrid<IHospitalRecordResult>
            gridId={GRID_ID}
            config={gridConfig}
            columns={columns}
            dataKey="id"
            wrapperClassName={gridStyles.oDataGridPage}
            resizableColumns
            onGridReady={onGridReady}
            rowClassName={row => getHospitalRecordColorRowClassName(row.color)}
            toolbarLeft={helpers => (
              <>
                <CommonButton
                  type="outline"
                  onClick={() => popupStore.setIsSwitchHospitalOpen(true)}
                  leftIcon={<ArrowLeftRight size={18} strokeWidth={2} />}
                  className={styles.toolbarBtnSwitch}
                >
                  Hastane Değiştir
                </CommonButton>
                <CommonButton
                  type="primary"
                  onClick={() => popupStore.setIsNewManuelRowOpen(true)}
                  leftIcon={<PlusCircle size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnManual}
                >
                  Yeni Veri Girişi
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={() => popupStore.setIsNewKnRowOpen(true)}
                  leftIcon={<Hash size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnKn}
                >
                  Künye Numarası İle Veri Girişi
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={() => popupStore.setIsNewQrCodeOpen(true)}
                  leftIcon={<QrCode size={18} strokeWidth={2} />}
                  className={styles.toolbarBtnQr}
                >
                  QR'dan Oku
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={() => void exportToExcel(helpers)}
                  leftIcon={<FileSpreadsheet size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={() => helpers.refreshGrid()}
                  leftIcon={<RotateCw size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
          />
        </div>
      </div>

      {popupStore.isNewManuelRowOpen && <NewRowManuel />}
      {popupStore.isNewQrCodeRowOpen && <NewRowQrCode />}
      {popupStore.isEditManuelRowOpen && <EditRowManually />}
      {popupStore.isSwitchHospitalOpen && <SwitchHospital />}
      {popupStore.isNewKnRowOpen && <NewRowFromKn />}
      <DeleteHospitalRecordDialog />
    </>
  );
};

export default observer(HospitalFiles);
